<template>
    <PackScene
        :active="active"
        :color="color"
        :tier="tier"
        :category="category"
        :layer="sceneLayer"
    />
</template>

<script>
    const packSceneLayer = ref(LAYER_PACK_SCENE);

    export const getNewPackSceneLayer = () => ++packSceneLayer.value;
</script>

<script setup>
    import { Mesh, WebGLRenderTarget } from 'three';
    import { computed, inject, onMounted, ref, watch } from 'vue';

    import { simpleMaterial, simplePlaneGeometry } from '@resn/gozer-three';
    import { clearStencil, renderStencilMask, renderStencilScene } from '@resn/gozer-three/utils';
    import { useRafBool, useRenderer, useResize, useThreeObject } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import PackScene from './PackScene.vue';
    import { LAYER_PACK_SCENE, LAYER_PACK_STENCIL } from '~/core/constants';

    const props = defineProps({
        active: { type: Boolean, default: true },
        category: { type: String, default: '' },
        tier: { type: Number, default: 1 },
        color: { type: String, default: '#ffd08b' },
    });

    const { renderer, scene, camera: defaultCamera } = useRenderer();
    const { camera } = inject('sceneCamera');
    const orbit = inject('orbit', ref(false));

    const { object } = useThreeObject(null, { name: 'PackSceneWrapper' });

    const fbo = new WebGLRenderTarget(1024, 1024);

    const sceneLayer = getNewPackSceneLayer();

    const setupStencilPlane = () => {
        const stencilPlane = new Mesh(simplePlaneGeometry, simpleMaterial('red'));
        stencilPlane.renderOrder = 1000;
        stencilPlane.scale.set(0.8, 1.5, 1);
        stencilPlane.name = 'StencilPlane';
        stencilPlane.layers.set(LAYER_PACK_STENCIL);
        stencilPlane.visible = false;

        object.add(stencilPlane);

        return stencilPlane;
    };

    const stencilPlane = setupStencilPlane();

    const canRender = computed(() => props.active);

    const setActive = (active) => {
        // console.log('🚀 ~ setActive ~ active:', active);
    };

    const onUpdate = () => {
        const prevRenderTarget = renderer.getRenderTarget();
        const prevClearAlpha = renderer.getClearAlpha();

        renderer.setRenderTarget(fbo);
        renderer.setClearAlpha(0);
        renderer.clear();

        const cam = orbit.value ? defaultCamera : camera;
        stencilPlane.visible = true;

        renderStencilMask({ renderer, scene, camera: cam, layer: LAYER_PACK_STENCIL, fbo });
        renderStencilScene({ renderer, scene, camera: cam, layer: sceneLayer, fbo });
        clearStencil({ renderer });

        stencilPlane.visible = false;

        renderer.setClearAlpha(prevClearAlpha);
        renderer.setRenderTarget(prevRenderTarget);
        cam.layers.set(0);
    };

    watch(() => props.active, setActive);

    useRafBool(canRender, onUpdate);

    useResize(({ width, height }) => {
        if (width === 0 || height === 0) return;

        const dpi = renderer.getPixelRatio();
        fbo.setSize(width * dpi, height * dpi);
    }, true);

    defineExpose({ fbo, object });
</script>
